/**
 * The photo viewer — tap any captured photo on the shelf (D-028).
 *
 * Brief §3: *"Tap any captured photo → a viewer with Note / Retake / Delete."*
 * Round 7 task 4 replaced Note with a label the photo itself carries: *"a
 * photo wears its label — typed in the viewer, worn on the card's bottom
 * quarter."* Deleting a required shot returns its placeholder (and its gap
 * logic); deleting an extra removes it.
 *
 * A dialog over a veil — the second true overlay in the app after the map
 * sheet, and built the same way: `role="dialog" aria-modal`, focus on open,
 * Escape and the veil close it. Everything it offers already existed on the
 * old photo tile; it is the tile's actions, in one place, at full size:
 *
 *   - **Label** (round 7 task 4, D-038; its Save button retired round 8 task
 *     5) — a caption up to 80 characters that the surveyor types for this ONE
 *     photo, shown under it on the shelf card (the band, the card's bottom
 *     quarter — every card's now, not only the front one; round 8 task 5).
 *     Tapping "✎ Label" swaps the card's title for an input in place, right
 *     here in `.pv__cap`. Round 8.1 task 5: the input holds its own DRAFT
 *     now, seeded from `photo.label` the instant the editor opens — every
 *     keystroke used to call `onLabelChange` (`store.setPhotoLabel`, a
 *     whole-document rewrite, a queued document push and an `emit()` that
 *     re-renders the whole survey tree) straight through, and on a 125-photo
 *     survey that turned typing one twelve-character caption into seconds of
 *     work the card visibly lagged behind. The draft is what the input shows
 *     and what every keystroke updates; `commit()` calls `onLabelChange` ONCE,
 *     and only when the trimmed draft actually differs from the photo's own
 *     saved label — Enter, Escape and losing focus all close the little
 *     editor by calling it first, and so does the dialog's own Done (and the
 *     veil behind it), so a tap that lands on Done before Enter ever did still
 *     saves what was typed rather than discarding it. This is `TextField`'s
 *     `useDraft` (D-037) in spirit, not its letter: that hook also has to
 *     adopt an external change arriving while the field still has focus,
 *     because `TextField` stays mounted the whole time a question is on
 *     screen; this editor is mounted ONLY while it is open, so a plain
 *     `useState` seeded at that moment already gets a fresh value every time
 *     and there is no external write to this same field to race. The editor
 *     used to carry its own "Save" button, named that only so the dialog
 *     would not show two controls both reading "Done" — but it saved nothing,
 *     the keystroke already had, so round 8 task 5 deleted it. This retired
 *     the old Note button — a photo's own label is now its caption, not a
 *     pointer to the section's notes.
 *
 *     Fix round 1: `photo` (and `label`) must come from the LIVE document on
 *     every render, not a snapshot taken when the viewer opened — the caller
 *     (`PhotoShelf`) re-derives the card it hands this component from
 *     `shelf.cards` by key every render rather than holding the card object
 *     in state, or reopening the editor within one session would re-seed the
 *     draft from the STALE label and the next commit would overwrite what
 *     had just been saved.
 *   - **Retake** — the OS picker for the same scope, opened synchronously in
 *     the tap (D-030 R10; round 7 task 2 — iOS only honours a file input's
 *     `.click()` inside the gesture that started it). The old photo is
 *     deleted only once the new one lands; cancelling the sheet keeps it —
 *     which is exactly why a parked photo's button reads "Take it again"
 *     (final review fix, minor 7) rather than "Delete it and take it again":
 *     nothing is deleted until the replacement actually lands, so the old
 *     words promised an act this button was never the one to perform.
 *   - **Delete** — a confirmation laid into the card, naming the shot (there is
 *     no browser modal anywhere in this app, D-012). Never touches R2.
 *   - **Try sending it again** — only when the engine says a retry can help;
 *     an evicted picture (canRetry false) offers Delete alone: one true action
 *     (D-025, "the evicted picture parks out loud").
 *   - **Full screen** (D-032) — the design authority, 2026-09-01: *"give
 *     users the ability to expand the picture to full screen in addition to
 *     Retaking or Deleting them."* A labelled button, first in the row, and
 *     the picture itself is the same button — the largest target on the card
 *     does the obvious thing. Opens `PhotoFullscreen` over this dialog; while
 *     it is up, Escape closes IT, not this. Offered only when the bytes are
 *     here: an evicted picture has nothing to expand. Round 8.1 task 4: the
 *     cards draw thumbnails, so this dialog is where the ORIGINAL is asked
 *     for — `original` arrives from `useOriginal` a beat after open, and
 *     until it does the picture here and in full screen is the thumbnail.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';

import type { PhotoRef } from '../../core/types';
import type { ParkedInfo } from '../state/photoShelf';
import { PhotoFullscreen } from './PhotoFullscreen';

export interface PhotoViewerProps {
  photo: PhotoRef;
  /** The card's title — the photo's own label if the surveyor set one, else
   *  the shot's name or `Photo N Extra` (the shelf model's `ShelfCard.title`). */
  label: string;
  /** True for a photo beyond the recommended list. */
  extra: boolean;
  /** The shot's framing note, if it has one — what has to be in the frame. */
  note?: string;
  /** Object URL of the thumbnail the card drew, or undefined when the bytes are gone. */
  src?: string;
  /**
   * Object URL of the ORIGINAL, once `useOriginal` has it (round 8.1, task
   * 4) — undefined until then, and the picture and full screen show the
   * thumbnail in the meantime rather than nothing.
   */
  original?: string;
  parked?: ParkedInfo;
  sectionTitle: string;
  /**
   * Round 7 task 10, ruling 12: on a submitted survey, Label, Retake and
   * Delete are gone — the office already has this photo and the surveyor
   * cannot change it. Full screen and Done stay: looking is not editing.
   */
  readOnly?: boolean;
  onClose: () => void;
  onRetake: () => void;
  onDelete: () => void;
  onRetry: () => void;
  /** The commit, once — `store.setPhotoLabel`, called at most once per edit. */
  onLabelChange: (next: string) => void;
}

export function PhotoViewer({
  photo,
  label,
  extra,
  note,
  src,
  original,
  parked,
  sectionTitle,
  readOnly = false,
  onClose,
  onRetake,
  onDelete,
  onRetry,
  onLabelChange,
}: PhotoViewerProps): ReactNode {
  const [confirming, setConfirming] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [editingLabel, setEditingLabel] = useState(false);
  // The draft the editor shows and every keystroke updates — see the header
  // comment. Seeded only when the editor actually opens (`openLabelEditor`
  // below), never here: this initial value is never read again once the
  // editor has opened once, so seeding it from `photo.label` at MOUNT would
  // be seeding it from whatever the label happened to be before the surveyor
  // ever touched this photo.
  const [draft, setDraft] = useState('');
  const closeButton = useRef<HTMLButtonElement | null>(null);
  const expandButton = useRef<HTMLButtonElement | null>(null);
  const gone = parked !== undefined && !parked.canRetry;
  // The original once it has arrived, the thumbnail until then.
  const shown = original ?? src;

  const openLabelEditor = (): void => {
    setDraft(photo.label ?? '');
    setEditingLabel(true);
  };

  /** Writes ONCE, and only if the draft actually says something new. */
  const commitLabel = (): void => {
    if (draft.trim() !== (photo.label ?? '')) onLabelChange(draft);
  };

  const closeLabelEditor = (): void => {
    commitLabel();
    setEditingLabel(false);
  };

  /**
   * The three doors that CLOSE the dialog — Done, the backdrop, Escape:
   * commit an open draft first, then close. Retake and Delete leave by their
   * own handlers below, which commit the same way: the first cut relied on
   * the editor's blur to commit before those two, which held only because
   * a tap on either button happened to blur the input first (final review's
   * fix wave, D-040 postscript).
   */
  const handleClose = (): void => {
    if (editingLabel) commitLabel();
    onClose();
  };

  /**
   * Retake: the draft is committed BEFORE the picker is asked for, because
   * the label rides to the replacement — `usePhotoCapture.capture` reads the
   * replaced photo's label off the live document when the new file lands,
   * and a draft still sitting in this input would not be on it.
   */
  const handleRetake = (): void => {
    if (editingLabel) commitLabel();
    onRetake();
  };

  /** Delete: committed for consistency — the label goes with the photo, but no door leaves a draft behind. */
  const handleDelete = (): void => {
    if (editingLabel) commitLabel();
    onDelete();
  };

  useEffect(() => {
    closeButton.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- focus on open only
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      // The full-screen overlay owns Escape while it is up.
      if (event.key === 'Escape' && !fullscreen) handleClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // `handleClose` closes over `draft`/`editingLabel`/`photo.label`, so it is
    // a new function every render — listed as a dep here (rather than
    // `onClose` alone, as before) so Escape at the window level always
    // commits the CURRENT draft rather than a stale one.
  }, [handleClose, fullscreen]);

  const closeFullscreen = (): void => {
    setFullscreen(false);
    window.setTimeout(() => expandButton.current?.focus(), 0);
  };

  return (
    <div className="pv" role="presentation" onClick={handleClose}>
      <div
        className="pv__card"
        role="dialog"
        aria-modal="true"
        aria-label={`${label} — ${sectionTitle}`}
        onClick={(event) => event.stopPropagation()}
      >
        {shown ? (
          <button
            type="button"
            className="pv__ph pv__ph--open"
            aria-label={`Full screen — ${label}`}
            onClick={() => setFullscreen(true)}
          >
            <img className="pv__img" src={shown} alt="" />
          </button>
        ) : (
          <div className="pv__ph">
            <span className="pv__img pv__img--none" aria-hidden="true">
              ▣
            </span>
          </div>
        )}
        <div className="pv__cap">
          {editingLabel && !readOnly ? (
            <div className="pv__label-edit">
              <input
                type="text"
                className="field__input pv__label-input"
                maxLength={80}
                aria-label="Photo label"
                value={draft}
                autoFocus
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={(event) => {
                  // Enter commits the draft (if it changed) and closes the
                  // little editor, the same as losing focus does.
                  if (event.key === 'Enter') closeLabelEditor();
                  // Escape commits too, and closes ONLY the editor: the
                  // viewer's own Escape listener (on `window`, above) must not
                  // take the dialog out from under it — a hardware keyboard on
                  // an iPad would lose the viewer on the way out of the box.
                  if (event.key === 'Escape') {
                    event.stopPropagation();
                    closeLabelEditor();
                  }
                }}
                onBlur={closeLabelEditor}
              />
            </div>
          ) : (
            <span className="pv__title" data-legibility="display">
              {label}
            </span>
          )}
        </div>

        {/* The photo's state, in the tile's own words; a parked photo prints the
         * engine's reason in full — it is the whole instruction. */}
        {parked ? (
          <p className="pv__stuck" data-legibility="helper">
            <b>Needs you.</b> {parked.reason}
          </p>
        ) : note ? (
          <p className="pv__note" data-legibility="helper">
            {note}
          </p>
        ) : null}

        {confirming && !readOnly ? (
          <div className="pv__confirm" role="alertdialog" aria-label={`Delete ${label}?`}>
            <p className="q" data-legibility="question">
              Delete this photo of {label}?
            </p>
            <p className="leadin" data-legibility="helper">
              {extra
                ? 'It goes off the survey. Nothing else changes.'
                : 'The shot goes back to "still to take" and the office sees it as not yet taken.'}
            </p>
            <div className="pv__actions">
              <button type="button" className="btn btn--ghost" onClick={() => setConfirming(false)}>
                Keep it
              </button>
              <button type="button" className="btn btn--danger" onClick={handleDelete}>
                Delete photo
              </button>
            </div>
          </div>
        ) : (
          <div className="pv__actions">
            {shown ? (
              <button
                type="button"
                className="btn btn--ghost pv__expand"
                onClick={() => setFullscreen(true)}
                ref={expandButton}
              >
                ⤢ Full screen
              </button>
            ) : null}
            {readOnly || gone || editingLabel ? null : (
              <button type="button" className="btn btn--ghost" onClick={openLabelEditor}>
                ✎ Label
              </button>
            )}
            {readOnly || gone ? null : (
              <button type="button" className="btn btn--ghost" onClick={handleRetake}>
                {parked ? 'Take it again' : 'Retake'}
              </button>
            )}
            {parked && parked.canRetry ? (
              <button type="button" className="btn btn--ghost" onClick={onRetry}>
                Try sending it again
              </button>
            ) : null}
            {/* Round 7 task 10, ruling 12: Delete is gone on a read-only
             * survey — the office already has this photo. Full screen and
             * Done are the only doors this dialog keeps open. */}
            {readOnly ? null : (
              <button
                type="button"
                className="btn btn--ghost pv__danger"
                onClick={() => (gone ? handleDelete() : setConfirming(true))}
              >
                {gone ? 'Delete it — the picture is gone from this phone' : 'Delete'}
              </button>
            )}
            <button type="button" className="btn btn--primary" onClick={handleClose} ref={closeButton}>
              Done
            </button>
          </div>
        )}

        {fullscreen && shown ? <PhotoFullscreen src={shown} label={label} onClose={closeFullscreen} /> : null}
      </div>
    </div>
  );
}
